import { config } from '../../config/env';

/**
 * Identificação do usuário do plano academy/JusRecruta (aluno).
 *
 * A comparação é por **id do plano** (`plan_details.id`, o id no jussub), com
 * `gateway_id` como segundo caminho porque parte das telas só carrega esse
 * campo. Nunca por nome: `plan_type` é o grupo de features
 * (starter/master/ultimate) e o nome comercial muda sem aviso — inferir plano
 * por substring de nome é a origem clássica de bug de liberação aqui.
 *
 * A lista vem de `REACT_APP_ACADEMY_PLAN_IDS` (ids separados por vírgula) para
 * que dev/QA e produção usem ids diferentes sem mudança de código. Lista vazia
 * = ninguém é aluno, então a feature nasce desligada.
 */

type PlanDetails =
  | {
      id?: number | string | null;
      gateway_id?: number | string | null;
    }
  | null
  | undefined;

const parseIds = (raw: string): number[] =>
  raw
    .split(',')
    .map((id) => Number(id.trim()))
    .filter((id) => Number.isFinite(id) && id > 0);

// A env é lida a cada chamada (e o parse fica em cache pela string crua) em vez
// de resolvida no import: no import, um `jest.mock` parcial de `config/env`
// derruba o módulo inteiro na carga.
let cache: { raw: string; ids: number[] } = { raw: '', ids: [] };

export function getAcademyPlanIds(): number[] {
  const raw = String(config?.academy?.planIds ?? '');
  if (raw !== cache.raw) cache = { raw, ids: parseIds(raw) };
  return cache.ids;
}

const matches = (
  value: number | string | null | undefined,
  ids: number[],
): boolean => {
  if (value === null || value === undefined || value === '') return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && ids.includes(numeric);
};

export function isAcademyPlan(planDetails: PlanDetails): boolean {
  const ids = getAcademyPlanIds();
  if (!planDetails || ids.length === 0) return false;
  return matches(planDetails.id, ids) || matches(planDetails.gateway_id, ids);
}

let cacheUsuarios: { raw: string; ids: number[] } = { raw: '', ids: [] };

/**
 * Atalho de desenvolvimento: trata os `user_id` listados em
 * `REACT_APP_ACADEMY_FORCE_USER_IDS` como alunos, seja qual for o plano deles.
 *
 * Existe porque o plano academy ainda não está no catálogo do jussub: sem ele
 * nenhuma conta real tem o plano, e a tela do aluno seria invisível para quem
 * está construindo. Assim dá para abrir a tela com a própria conta.
 *
 * **Ignorado em produção**, por build. Uma lista de ids que libera plano é
 * exatamente o tipo de coisa que vaza para o ambiente errado se depender só de
 * alguém não preencher a variável.
 */
export function isAcademyForcedUser(userId: number | string | undefined): boolean {
  if (process.env.NODE_ENV === 'production') return false;

  const raw = String(config?.academy?.forceUserIds ?? '');
  if (raw !== cacheUsuarios.raw) cacheUsuarios = { raw, ids: parseIds(raw) };

  return matches(userId, cacheUsuarios.ids);
}
